/**
 * A borrower's quarter: the header (DOP-626, after the deal memo's).
 *
 * Where you are (the borrower, a back arrow to the board), what
 * state the quarter is in (draft / reviewed / locked, the version) with its
 * provenance on one line, and the three things to do: chat about it,
 * download it, lock it. "Lock quarter" stays visible but disabled while a
 * check is open, with the reason in its title: hiding it would leave the user
 * looking for the button the workflow ends in.
 *
 * Under it, while checks are open, one banner says how many stand between
 * this quarter and a lock, with Start checks — the memo's pattern.
 */

import { Download, Lock, Sparkles } from "lucide-react";
import React from "react";

import type { MonitoringRun } from "../../types/monitoring";
import { formatEpochMs } from "./runFormat";
import BackButton from "../BackButton";

export interface RunHeaderProps {
	run: MonitoringRun;
	/** Back to the period board this run belongs to. */
	onOpenBoard: () => void;
	onDownload: () => void;
	downloading: boolean;
	onOpenLock: () => void;
	/** Opens the Checks tab, from the banner. */
	onOpenChecks?: () => void;
	/** The Analyst, when this deployment has it. */
	analyst?: { open: boolean; onToggle: () => void } | null;
}

const BUTTON =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] disabled:cursor-not-allowed disabled:opacity-50";

const RunHeader: React.FC<RunHeaderProps> = ({
	run,
	onOpenBoard,
	onDownload,
	downloading,
	onOpenLock,
	onOpenChecks,
	analyst = null,
}) => {
	const locked = run.status === "locked";
	const requiredFigures = run.figures.filter((figure) => figure.kind !== "derived").length;
	const checks = run.gaps_open;
	const lockBlocked = checks > 0 || run.generating;
	const lockTitle = run.generating
		? "This run is still being generated"
		: checks > 0
			? `Answer ${checks} open check${checks === 1 ? "" : "s"} first`
			: undefined;
	const statusLabel = locked
		? `Locked${run.locked_at ? ` ${formatEpochMs(run.locked_at)}` : ""}`
		: `${run.status === "reviewed" ? "Reviewed" : "Draft"} · v${run.version}`;

	return (
		<>
			{/* No breadcrumb: the back arrow already leads to the board, and the
			    period is named on the Lock button. */}
			<header className="px-6 pb-3 pt-5">
				<div className="flex flex-wrap items-center gap-3">
					<BackButton onClick={onOpenBoard} label="Back to the period board" />
					<h1 className="min-w-0 truncate text-xl font-semibold text-[#111111]">{run.borrower.name}</h1>
					<span
						className={`inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ${
							locked ? "bg-[#E6F3F3] text-[#004C4B]" : "bg-[#f2f2f2] text-[#3f4247]"
						}`}
						data-testid="run-status"
					>
						{locked && <Lock className="h-3 w-3" aria-hidden />}
						{statusLabel}
					</span>
					<span className="text-xs text-[#6B7280]" data-testid="run-provenance">
						{run.generation_error ? (
							<span className="text-red-700">{run.generation_error}</span>
						) : run.generating ? (
							"Reading the package…"
						) : (
							[
								run.generated_at ? `Read ${formatEpochMs(run.generated_at)}` : null,
								`from ${run.package.length} ${run.package.length === 1 ? "document" : "documents"}`,
								`${requiredFigures} figures`,
							]
								.filter(Boolean)
								.join(" · ")
						)}
					</span>
					<div className="ml-auto flex items-center gap-2">
						{analyst && (
							<button
								type="button"
								onClick={analyst.onToggle}
								aria-pressed={analyst.open}
								className={`${BUTTON} ${
									analyst.open
										? "border-[#004C4B] bg-[#E6F3F3] text-[#004C4B]"
										: "border-gray-200 bg-white text-[#1F2937] hover:bg-gray-50"
								}`}
							>
								<Sparkles className="h-4 w-4" aria-hidden />
								Chat
							</button>
						)}
						<button
							type="button"
							onClick={onDownload}
							disabled={downloading}
							className={`${BUTTON} border-gray-200 bg-white text-[#1F2937] hover:bg-gray-50`}
						>
							<Download className="h-4 w-4" aria-hidden />
							{downloading ? "Preparing…" : "Download .xlsx"}
						</button>
						{!locked && (
							<button
								type="button"
								onClick={onOpenLock}
								disabled={lockBlocked}
								title={lockTitle}
								className={`${BUTTON} border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]`}
							>
								<Lock className="h-4 w-4" aria-hidden />
								Lock {run.period.label}
							</button>
						)}
					</div>
				</div>
			</header>

			{!locked && !run.generating && checks > 0 && (
				<div
					className="mx-6 mb-2 flex items-center gap-3 rounded-xl border border-[#fedf89] bg-[#fffaeb] px-4 py-2.5"
					data-testid="checks-banner"
				>
					<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#fedf89] bg-white text-[13px] font-bold text-[#b54708]">
						{checks}
					</span>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-[#7a2e0e]">
							{checks === 1 ? "1 check" : `${checks} checks`} before you can lock {run.period.label}
						</p>
						<p className="text-[12.5px] text-[#93370d]">
							{checks === 1 ? "A figure the workbook needs isn't" : "Figures the workbook needs aren't"} in the
							package. Answer or accept each one.
						</p>
					</div>
					{onOpenChecks && (
						<button
							type="button"
							onClick={onOpenChecks}
							className={`${BUTTON} border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]`}
						>
							Start checks
						</button>
					)}
				</div>
			)}
		</>
	);
};

export default RunHeader;
